import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { TIERS, STATUSES, getTiers } from "../../lib/personnel";
import { useData } from "../../context/DataContext";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney, fmtDate, fmtMonthShort, contractLabel } from "../../lib/format";
import { AlertTriangle, History, KeyRound, ExternalLink } from "lucide-react";

export default function EditPersonDrawer({ open, person, onClose, onSaved, projects = [], months = [], login, onCreateAccount }) {
  const [form, setForm] = useState(person || {});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { rawData } = useData();
  const allTiers = getTiers(rawData);

  useEffect(() => {
    if (person) setForm(structuredClone(person));
  }, [person]);

  // Per-person allocations across all 12 months — looked up via linkedPersonId
  // (matches a project allocation by data.people[].id).
  const linkedId = form.linkedPersonId || form.id;
  const personMonthly = useMemo(() => {
    const totals = Object.fromEntries(months.map((m) => [m, 0]));
    const linkedProjects = [];
    for (const p of projects) {
      const a = (p.allocations || []).find((x) => x.personId === linkedId);
      if (!a) continue;
      linkedProjects.push(p);
      for (const m of months) {
        totals[m] += Number(a.load?.[m] || 0);
      }
    }
    return { totals, linkedProjects };
  }, [projects, months, linkedId]);

  if (!person) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.fullName?.trim()) {
      toast.error("Full name required");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/personnel/${encodeURIComponent(form.id)}`, form);
      toast.success("Saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = () => {
    if (!login) return;
    onClose();
    navigate(`/admin/users?highlight=${encodeURIComponent(login.email)}`);
  };

  const depart = async () => {
    if (!confirm(`Mark ${form.fullName} as departed? Their existing project allocations stay intact, but they will not appear in active dropdowns.`)) return;
    setBusy(true);
    try {
      await api.put(`/personnel/${encodeURIComponent(form.id)}/depart`);
      toast.success("Marked as departed");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const history = (form.rateHistory || []).slice().reverse();
  const ratesChanged =
    Number(form.monthlyRate  || 0) !== Number(person.monthlyRate  || 0) ||
    Number(form.hourlyRate   || 0) !== Number(person.hourlyRate   || 0);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="!max-w-2xl w-full overflow-y-auto" data-testid="edit-person-drawer">
        <SheetHeader>
          <SheetTitle className="font-display tracked text-base">
            {form.fullName} · <span className="text-[var(--soft-fg)] font-mono-num">{form.tier}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Basics */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name" className="col-span-2">
              <input className={inp} value={form.fullName || ""} onChange={(e) => update({ fullName: e.target.value })} data-testid="edit-fullname" />
            </Field>
            <Field label="Tier">
              <select className={inp} value={form.tier} onChange={(e) => update({ tier: e.target.value })} data-testid="edit-tier">
                {allTiers.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className={inp} value={form.status} onChange={(e) => update({ status: e.target.value })} data-testid="edit-status">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          {/* Rates */}
          <div>
            <div className="eyebrow mb-2">Rates</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Monthly rate">
                <input type="number" className={`${inp} font-mono-num text-right`} value={form.monthlyRate || 0} onChange={(e) => update({ monthlyRate: Number(e.target.value) || 0 })} data-testid="edit-monthly" />
              </Field>
              <Field label="Hourly rate">
                <input type="number" step="0.01" className={`${inp} font-mono-num text-right`} value={form.hourlyRate || 0} onChange={(e) => update({ hourlyRate: Number(e.target.value) || 0 })} data-testid="edit-hourly" />
              </Field>
            </div>
            {ratesChanged && (
              <div className="mt-2 text-[11px] text-[#B45309] inline-flex items-center gap-1">
                <AlertTriangle size={11} /> Saving will append a row to rate history.
              </div>
            )}
          </div>

          {/* Allocation drill-down */}
          {months.length > 0 && (
            <div>
              <div className="eyebrow mb-2">Allocation · next {months.length} months</div>
              <div className="border border-[var(--rule)] overflow-x-auto thin-scroll">
                <table className="text-xs border-collapse w-full">
                  <thead>
                    <tr className="bg-[var(--paper-soft)]">
                      {months.map((m) => <th key={m} className="px-2 py-1.5 eyebrow text-right">{fmtMonthShort(m)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {months.map((m) => {
                        const v = personMonthly.totals[m] || 0;
                        const band = v > 1.0 ? "bg-[#FBF3F2] text-[#6B2F2A]" : v >= 0.8 ? "bg-[var(--ink)] text-white" : v > 0 ? "bg-[#F4EEDC]" : "";
                        return <td key={m} className={`px-2 py-1.5 font-mono-num text-right ${band}`}>{v > 0 ? `${Math.round(v*100)}%` : ""}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
              {personMonthly.linkedProjects.length > 0 ? (
                <div className="mt-3 text-xs space-y-1.5">
                  {personMonthly.linkedProjects.map((p) => (
                    <div key={p.id} className="flex items-center gap-3" data-testid={`linked-project-${p.id}`}>
                      <span className="text-[var(--ink)] flex-1">{p.name}</span>
                      <span className="text-[var(--soft-fg)] font-mono-num">{p.status || "—"}</span>
                      <span className={`chip ${p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no"}`}>{contractLabel(p.contract)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 text-xs text-[var(--soft-fg)] italic">
                  No project allocations linked. (Project allocations reference person id <code>{linkedId}</code> — set <strong>linkedPersonId</strong> below if this person is in existing project rows under a different id.)
                </div>
              )}
              <Field label="Linked person id (advanced)" className="mt-3">
                <input className={`${inp} font-mono-num text-xs`} value={form.linkedPersonId || ""} onChange={(e) => update({ linkedPersonId: e.target.value })} placeholder="e.g. mark or mark_craven — must match data.people[].id" data-testid="edit-linkedperson" />
              </Field>
            </div>
          )}

          {/* Linked login */}
          <div data-testid="login-section">
            <div className="eyebrow mb-2 inline-flex items-center gap-1.5"><KeyRound size={12} /> Login</div>
            {login ? (
              <div className="border border-[var(--rule)] p-3 text-xs space-y-1.5">
                <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono-num">
                  <span data-testid="login-email">{login.email}</span>
                  <span className="uppercase tracking-wider text-[var(--soft-fg)]" data-testid="login-role">{login.role}</span>
                  <span className={login.status === "disabled" ? "text-[#6B2F2A]" : "text-[#2C4A38]"} data-testid="login-status">
                    {login.status === "disabled" ? "disabled" : "active"}
                  </span>
                  <span className="text-[var(--soft-fg)]" data-testid="login-last">
                    last login {login.last_login ? fmtDate(login.last_login) : "never"}
                  </span>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={resetPassword}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule-strong)] text-[10px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
                    data-testid="reset-password-button"
                  >
                    Reset password <ExternalLink size={10} />
                  </button>
                  <span className="text-[10px] text-[var(--soft-fg)]">Opens Users page to reset. Passwords are hashed — they can be reset, not viewed.</span>
                </div>
              </div>
            ) : (
              <div className="border border-[var(--rule)] p-3 text-xs flex items-center justify-between gap-3">
                <span className="text-[var(--soft-fg)]" data-testid="no-login-indicator">No login for this person.</span>
                <button
                  onClick={() => onCreateAccount?.(person)}
                  disabled={busy || form.status === "departed"}
                  className="px-3 py-1.5 bg-[var(--ink)] text-white text-[10px] font-display tracked-tight uppercase disabled:opacity-40"
                  data-testid="create-account-button"
                >
                  Create account
                </button>
              </div>
            )}
          </div>

          {/* Notes */}
          <Field label="Notes (admin-only)">
            <textarea className={`${inp} h-20`} value={form.notes || ""} onChange={(e) => update({ notes: e.target.value })} data-testid="edit-notes" />
          </Field>

          {/* Rate history */}
          {history.length > 0 && (
            <div>
              <div className="eyebrow mb-2 inline-flex items-center gap-1.5"><History size={12} /> Rate &amp; tier history</div>
              <div className="border border-[var(--rule)]" data-testid="rate-history">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--paper-soft)] eyebrow text-left">
                    <tr>
                      <th className="px-3 py-1.5">Date</th>
                      <th className="px-3 py-1.5">Change</th>
                      <th className="px-3 py-1.5 text-right">Monthly</th>
                      <th className="px-3 py-1.5 text-right">Hourly</th>
                      <th className="px-3 py-1.5">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.date || "date"}-${h.monthlyRate ?? "rate"}-${i}`} className="border-t border-[var(--rule)]" data-testid={`rate-history-row-${i}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(h.date)}</td>
                        <td className="px-3 py-1.5" data-testid={`rate-history-change-${i}`}>
                          {h.previousTier ? (
                            <span className="text-[var(--ink)]">
                              {h.previousTier} <span className="text-[var(--soft-fg)]">→</span> {h.tier}
                              {h.changeType === "tier+rate" && <span className="text-[var(--soft-fg)]"> · rate</span>}
                            </span>
                          ) : (
                            <span className="text-[var(--soft-fg)]">
                              Rate{h.tier ? ` · ${h.tier}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono-num">{fmtMoney(h.monthlyRate)}</td>
                        <td className="px-3 py-1.5 text-right font-mono-num">${Number(h.hourlyRate || 0).toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-[var(--soft-fg)]">{String(h.changedBy || "").split("@")[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          {form.status !== "departed" ? (
            <button onClick={depart} disabled={busy} className="text-xs text-[#6B2F2A] hover:underline" data-testid="depart-button">
              Mark as departed
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-xs uppercase font-display tracked-tight text-[var(--soft-fg)] hover:text-[var(--ink)]">Cancel</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 bg-[var(--ink)] text-white text-xs uppercase font-display tracked-tight disabled:opacity-50" data-testid="edit-person-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const inp = "w-full px-3 py-2 border border-[var(--rule)] text-sm bg-white focus:border-[var(--ink)] outline-none";

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
